import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Box, Typography } from '@mui/material';
import PaymentAnalyticsDetailsTable from './PaymentAnalyticsDetailsTable';
import { PaymentAnalyticsIcon } from '../../icons';
import StatsCardPayment from './StatsCardPayment';
import BasicRangeShortcuts from '../date-range-picker/BasicRangeShortcuts';
import CountrySelect from '../country-select/CountrySelect';
import FilterState from '../country-select/FilterState';
import PlatformMultiSelect from './PlatformMultiSelect';
import UserFilterDropdown from './UserFilterDropdown';
import StatsCardPaymentSkeleton from './StatsCardPaymentSkeleton';
import useLoader from '../../hooks/useLoader';
import { fetchPaymentAnalyticsStats } from '../../api/paymentAnalytics';
import { paymentCardData } from './paymentCardData';

// The shared country/date pickers are grey elsewhere; on this page they sit in a white bar like the other filters.
const filterBarSx = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 1.5,
  '& .MuiAutocomplete-root .MuiInputBase-root': { background: '#fff !important' },
  '& .MuiAutocomplete-root .MuiOutlinedInput-notchedOutline': { border: '1px solid #E8E8E8 !important' },
  '& .reportrange': { background: '#fff !important', border: '1px solid #E8E8E8', borderRadius: '8px !important', height: 40, gap: 1 },
};

const PaymentAnalytics = () => {
  const reduxFilter = useSelector((state) => state.filter);
  const { loading, startLoading, stopLoading } = useLoader();

  const [data, setData] = useState(paymentCardData);
  const [platforms, setPlatforms] = useState([]);
  const [paymentUserFilter, setPaymentUserFilter] = useState([]);
  const [activeCard, setActiveCard] = useState('paywallViewed');

  const filter = useMemo(
    () => ({
      dateRange: reduxFilter.dateRange,
      country: reduxFilter.country,
      state: reduxFilter.state,
      city: reduxFilter.city,
      platforms,
      paymentUserFilter,
    }),
    [reduxFilter.dateRange, reduxFilter.country, reduxFilter.state, reduxFilter.city, platforms, paymentUserFilter]
  );

  const fetchData = async () => {
    try {
      startLoading();
      await fetchPaymentAnalyticsStats(filter, paymentCardData, setData);
    } catch (err) {
      console.error('Error while loading payment analytics data', err);
    } finally {
      stopLoading();
    }
  };

  useEffect(() => {
    fetchData();
  }, [JSON.stringify(filter)]);

  return (
    <div>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={2.5}>
        <Box display="flex" alignItems="center" gap={1.5}>
          <PaymentAnalyticsIcon />
          <Box>
            <Typography sx={{ fontSize: 24, fontWeight: 700, color: '#111', lineHeight: 1.2 }}>Payment Analytics</Typography>
            <Typography sx={{ fontSize: 14, color: '#6A7282' }}>How members move from the paywall to a paid plan.</Typography>
          </Box>
        </Box>
        <Box sx={filterBarSx}>
          <BasicRangeShortcuts id="range1" />
        </Box>
      </Box>

      {/* Filters */}
      <Box sx={{ ...filterBarSx, mb: 2.5 }}>
        <CountrySelect />
        {reduxFilter.country ? <FilterState /> : null}
        <PlatformMultiSelect value={platforms} onChange={setPlatforms} />
        <UserFilterDropdown value={paymentUserFilter} onChange={setPaymentUserFilter} />
      </Box>

      {/* Funnel cards — the selected one drives the table below */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
        {data.map((item) =>
          loading ? (
            <StatsCardPaymentSkeleton key={item.iconKey} />
          ) : (
            <StatsCardPayment
              key={item.iconKey}
              iconKey={item.iconKey}
              title={item.title}
              value={item.value}
              change={item.change}
              isPositive={item.isPositive}
              subtitleRight={item.subtitleRight}
              isActive={String(activeCard) === String(item.iconKey)}
              onClick={() => setActiveCard(item.iconKey)}
            />
          )
        )}
      </Box>

      <PaymentAnalyticsDetailsTable filter={filter} activeCard={activeCard} />
    </div>
  );
};

export default PaymentAnalytics;
